import React from 'react';
import { Button, InputNumber, Select, Space, Switch, Tag } from 'antd';
import { AlertOutlined, BugOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import type { SettingsController } from '../../hooks/useSettings';
import { CardHint, Field, SaveButton, SettingsCard, CONTROL_WIDTH } from './parts';

export const TicketSystemCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard title="Ticket System" icon={<AlertOutlined />} loading={s.loading}>
    <Field
      label={
        <Space size={6}>
          Auto-created incident tickets
          <Tag color={s.ticketsEnabled ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
            {s.ticketsEnabled ? 'Active' : 'Inactive'}
          </Tag>
        </Space>
      }
      hint="Automatically create incident tickets when health checks detect failures. Tickets are grouped by error type and country."
    >
      <Switch
        size="small"
        checked={s.ticketsEnabled}
        onChange={s.handleToggleTickets}
        loading={s.togglingTickets}
        checkedChildren="On"
        unCheckedChildren="Off"
      />
    </Field>
  </SettingsCard>
);

export const ReportsCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Reports"
    icon={<FileTextOutlined />}
    loading={s.loading}
    extra={<SaveButton onClick={s.handleSaveReport} loading={s.savingReport} />}
  >
    <CardHint>
      Configure automatic report generation interval and storage limits.
      Reports aggregate health data for networks and proxies separately.
    </CardHint>
    <Field
      label="Generation interval"
      hint="How often to automatically generate health reports. Set to &quot;Disabled&quot; for manual generation only."
    >
      <Select
        size="small"
        value={s.reportIntervalMinutes}
        onChange={s.setReportIntervalMinutes}
        style={{ width: CONTROL_WIDTH }}
        options={[
          { value: 0, label: 'Disabled (manual only)' },
          { value: 15, label: '15 minutes' },
          { value: 30, label: '30 minutes' },
          { value: 60, label: '1 hour' },
          { value: 180, label: '3 hours' },
          { value: 360, label: '6 hours' },
          { value: 720, label: '12 hours' },
          { value: 1440, label: '24 hours' },
        ]}
      />
    </Field>
    <Field
      label="Max stored reports"
      hint="Maximum number of reports to keep. Oldest reports are deleted when the limit is exceeded."
    >
      <InputNumber
        size="small"
        value={s.maxReports}
        onChange={(val) => s.setMaxReports(val ?? 1000)}
        min={10}
        max={10000}
        step={100}
        style={{ width: CONTROL_WIDTH }}
      />
    </Field>
  </SettingsCard>
);

export const DebugDumpCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Debug Dump"
    icon={<BugOutlined />}
    extra={
      <Button size="small" icon={<DownloadOutlined />} onClick={s.handleDebugDump} loading={s.dumpLoading}>
        Download
      </Button>
    }
  >
    <CardHint>
      Download a full diagnostic snapshot (settings, servers, health states, flapping states,
      worker stats, recent check logs, Redis info). Use this for troubleshooting and analysis.
    </CardHint>
  </SettingsCard>
);
